/**
 * App root — installs QueryClient + TgContext provider + RouterProvider.
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { type ReactElement, useEffect, useMemo, useState } from "react";

import { initTelegram, type TgLaunchContext } from "@/lib/tg";
import { router } from "@/router";
import { TgContext } from "@/state/tg";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function App(): ReactElement {
  const [tg, setTg] = useState<TgLaunchContext | null>(null);

  useEffect(() => {
    setTg(initTelegram());
  }, []);

  const ctx = useMemo(
    () =>
      tg ?? {
        inTelegram: false,
        initData: null,
        user: null,
        colorScheme: "dark" as const,
        platform: "web",
      },
    [tg],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TgContext.Provider value={ctx}>
        <RouterProvider router={router} />
        <Analytics />
        <SpeedInsights />
      </TgContext.Provider>
    </QueryClientProvider>
  );
}
